import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Badge, Dropdown, Table, useLocalizeMessage } from 'libs.nucleus';
import { useImportState } from './contexts/import_state.context';
import { useSourceTranslations } from './hooks/use_source_translations';
import { AssetVersion } from '../asset_version/asset_version.component';
export const EligibleLocales = ({ sourceLocalizationId }) => {
    const translate = useLocalizeMessage();
    const { setSelectedEligibleLocales } = useImportState();
    const { sourceMap, localizationDocument } = useSourceTranslations(sourceLocalizationId);
    const [selectedVersions, setSelectedVersions] = useState({});
    const [selectedRows, setSelectedRows] = useState([]);
    const supportedLocales = localizationDocument?.supported_locales;
    // Helper function to get the latest version for a locale
    const getLatestVersion = useCallback((locale, history) => {
        // First check if there's a supported version for this locale
        const localeVersion = supportedLocales?.[locale]?.version;
        if (localeVersion !== undefined) {
            return localeVersion;
        }
        // Fallback to the highest version in history (last item since it's sorted)
        return history.length > 0 ? history[history.length - 1].version : null;
    }, [supportedLocales]);
    // Convert sourceMap to an array of objects
    const data = useMemo(() => Array.from(sourceMap.entries()).map(([locale, history]) => {
        return {
            locale,
            history,
        };
    }), [sourceMap]);
    // No default selection - selectedVersions and selectedLocales remain empty until user interaction
    const handleVersionChange = useCallback((locale, version) => {
        // Update the selected version for this locale
        setSelectedVersions((prev) => ({
            ...prev,
            [locale]: version,
        }));
        // Only add to selectedLocales if the row is selected
        const isRowSelected = selectedRows.some((rowId) => {
            const index = typeof rowId === 'number' ? rowId : parseInt(rowId, 10);
            if (!isNaN(index) && index >= 0 && index < data.length) {
                return data[index].locale === locale;
            }
            return false;
        });
        if (isRowSelected) {
            setSelectedEligibleLocales((prev) => {
                const filtered = prev.filter((item) => item.locale !== locale);
                return [...filtered, { locale, version }];
            });
        }
    }, [selectedRows, data]);
    const columns = useMemo(() => [
        {
            header: translate('Locale'),
            accessorKey: 'locale',
        },
        {
            header: translate('Selected Version'),
            accessorKey: 'history',
            size: 20,
            cell: ({ row }) => {
                const selectedVersion = selectedVersions[row.original.locale];
                // Get the latest version for this locale
                const latestVersion = getLatestVersion(row.original.locale, row.original.history);
                // Create selected label with latest tag if applicable
                const selectedLabel = selectedVersion
                    ? latestVersion !== null && selectedVersion === latestVersion
                        ? `v${selectedVersion} (Latest)`
                        : `v${selectedVersion}`
                    : translate('Select version');
                const dropdownItems = row.original.history.map((history) => {
                    const isLatest = latestVersion !== null && history.version === latestVersion;
                    return {
                        id: `${row.original.locale}-${history.version}`,
                        content: (_jsxs("div", { className: 'w-[8.5rem] flex items-center gap-2', children: [_jsx(AssetVersion, { currentVersion: { version: history.version, status: history.status } }), isLatest && _jsx(Badge, { label: 'Latest', variant: 'common' })] })),
                        onClick: () => handleVersionChange(row.original.locale, history.version),
                    };
                });
                return (_jsx("section", { className: 'w-24', children: _jsx(Dropdown, { position: 'bottom-end', items: dropdownItems, label: selectedLabel, width: 'sm' }) }));
            },
        },
    ], [selectedVersions, handleVersionChange, translate, getLatestVersion]);
    const onRowSelectionChange = useCallback((selectedRows) => {
        // Update selectedRows state
        setSelectedRows(selectedRows);
        // TableRowId is likely a string or number, so find the corresponding data row by index
        const selectedLocales = selectedRows
            .map((rowId) => {
            // If TableRowId is a string, parse to number if needed
            const index = typeof rowId === 'number' ? rowId : parseInt(rowId, 10);
            // Defensive: check index bounds
            if (!isNaN(index) && index >= 0 && index < data.length) {
                return data[index].locale;
            }
            return undefined;
        })
            .filter((locale) => typeof locale === 'string');
        setSelectedEligibleLocales((prev) => {
            // Create a new array with only the currently selected locales
            const newSelectedLocales = selectedLocales
                .map((locale) => {
                const dataItem = data.find((item) => item.locale === locale);
                if (!dataItem) {
                    return null;
                }
                // Check if this locale already has a selected version in the previous state
                const existingVersion = prev.find((item) => item.locale === locale)?.version;
                if (existingVersion) {
                    // Keep the existing version (user's choice)
                    return { locale, version: existingVersion };
                }
                // Check if this locale has a version in selectedVersions (user selected version before row selection)
                const userSelectedVersion = selectedVersions[locale];
                if (userSelectedVersion) {
                    // Use the user-selected version
                    return { locale, version: userSelectedVersion };
                }
                // Use latest version only for newly selected rows with no user preference
                const latestVersion = getLatestVersion(locale, dataItem.history);
                return latestVersion !== null ? { locale, version: latestVersion } : null;
            })
                .filter((item) => item !== null);
            return newSelectedLocales;
        });
        // Update selectedVersions state to match - only keep versions for currently selected locales
        setSelectedVersions((prev) => {
            const updated = {};
            selectedLocales.forEach((locale) => {
                // Keep existing version if available (preserves user's version selection)
                if (prev[locale]) {
                    updated[locale] = prev[locale];
                }
                else {
                    // Only set to latest version if user hasn't selected a version yet
                    const dataItem = data.find((item) => item.locale === locale);
                    if (dataItem) {
                        const latestVersion = getLatestVersion(locale, dataItem.history);
                        if (latestVersion !== null) {
                            updated[locale] = latestVersion;
                        }
                    }
                }
            });
            return updated;
        });
    }, [data, getLatestVersion, selectedVersions]);
    return (_jsx("div", { "data-testid": 'eligible-locales-table', children: _jsx(Table, { data: data, columns: columns, isSelectable: true, onRowSelectionChange: onRowSelectionChange }) }));
};
